import dayjs from "dayjs";
import type { RecurrenceByDay, RecurrenceRule } from "@/lib/humhub-api/types";

const MONTHLY_MISMATCH_MESSAGE =
  "For MONTHLY recurrence, start date day (%d) must match byMonthDay (%d)";
export const WEEKDAY_ORDER: RecurrenceByDay[] = [
  "SU",
  "MO",
  "TU",
  "WE",
  "TH",
  "FR",
  "SA",
];
export const WEEKDAY_LABEL_KEYS: Record<RecurrenceByDay, string> = {
  SU: "common.sunday",
  MO: "common.monday",
  TU: "common.tuesday",
  WE: "common.wednesday",
  TH: "common.thursday",
  FR: "common.friday",
  SA: "common.saturday",
};

export const dayjsWeekdayToRecurrence = (
  dayjsIndex: number,
): RecurrenceByDay => {
  return WEEKDAY_ORDER[dayjsIndex] ?? "MO";
};

export const recurrenceWeekdayToDayjsIndex = (
  weekday: RecurrenceByDay,
): number => {
  const index = WEEKDAY_ORDER.indexOf(weekday);
  return index >= 0 ? index : 0;
};

export interface RecurrenceValidationResult {
  isValid: boolean;
  errors: string[];
}

type TranslationFn = (
  key: string,
  values?: Record<string, string | number>,
) => string;

const buildErrorMessage = (
  translate: TranslationFn | undefined,
  key: string,
  fallback: string,
  values?: Record<string, string | number>,
) => {
  if (translate) {
    return translate(key, values);
  }
  if (!values) return fallback;
  return Object.entries(values).reduce(
    (message, [placeholder, value]) =>
      message.replace(`{${placeholder}}`, String(value)),
    fallback,
  );
};

const formatDate = (date: dayjs.Dayjs) => date.format("YYYY-MM-DD");

export const getMonthlyWeekInfo = (date: dayjs.Dayjs) => {
  const weekIndex = Math.floor((date.date() - 1) / 7) + 1; // 1-5
  const isLast = date.add(1, "week").month() !== date.month();
  return { weekIndex, isLast };
};

export const getAllowedSetPositions = (
  weekIndex: number,
  isLast: boolean,
): number[] => {
  const options = [weekIndex];
  if (isLast && !options.includes(-1)) {
    options.push(-1);
  }
  return options;
};

const matchesSetPos = (
  date: dayjs.Dayjs,
  target: number | null | undefined,
) => {
  if (target === undefined || target === null) return false;
  const { weekIndex, isLast } = getMonthlyWeekInfo(date);
  if (target === -1) return isLast;
  return weekIndex === target;
};

const buildGenerationLimit = (
  startDate: string,
  freq: RecurrenceRule["freq"],
) => {
  const start = dayjs(startDate);
  switch (freq) {
    case "DAILY":
      return start.endOf("month");
    case "WEEKLY":
      return start.endOf("month").add(5, "month").endOf("month");
    case "MONTHLY":
      return start.endOf("month").add(11, "month").endOf("month");
    default:
      return start;
  }
};

export const getRecurrenceGenerationLimit = (
  startDate: string,
  freq: RecurrenceRule["freq"],
) => {
  return buildGenerationLimit(startDate, freq);
};

const computeLastOccurrence = (
  rule: RecurrenceRule,
  startDate: string,
): dayjs.Dayjs | null => {
  if (rule.until) {
    return dayjs(rule.until);
  }
  if (rule.count && rule.count > 0) {
    const start = dayjs(startDate);
    const offset = rule.count - 1;
    if (rule.freq === "DAILY") {
      return start.add(offset, "day");
    }
    if (rule.freq === "WEEKLY") {
      return start.add(offset, "week");
    }
    if (rule.freq === "MONTHLY") {
      return start.add(offset, "month");
    }
  }
  return null;
};

export const validateRecurrenceRule = (
  rule: RecurrenceRule | null | undefined,
  startDate: string,
  eventEndDate?: string,
  translate?: TranslationFn,
  minUntilDate?: string,
): RecurrenceValidationResult => {
  if (!rule) {
    return { isValid: true, errors: [] };
  }

  const errors: string[] = [];
  const { freq, interval, byDay, byMonthDay, bySetPos, until, count } = rule;
  const intervalValue = interval ?? 1; // APIは1固定だが欠落している場合は1として扱う

  if (!["DAILY", "WEEKLY", "MONTHLY"].includes(freq)) {
    errors.push(
      buildErrorMessage(
        translate,
        "common.recurrenceErrorUnsupportedFreq",
        "Only DAILY, WEEKLY, or MONTHLY recurrence is supported",
      ),
    );
  }

  if (intervalValue !== 1) {
    errors.push(
      buildErrorMessage(
        translate,
        "common.recurrenceErrorInterval",
        "Interval must be 1",
      ),
    );
  }

  if ((until && count) || (!until && !count)) {
    errors.push(
      buildErrorMessage(
        translate,
        "common.recurrenceErrorUntilOrCountExclusive",
        "Either until or count must be specified (exclusively)",
      ),
    );
  }

  if (freq === "WEEKLY") {
    if (!byDay) {
      errors.push(
        buildErrorMessage(
          translate,
          "common.recurrenceErrorByDayRequired",
          "byDay is required for WEEKLY recurrence",
        ),
      );
    }
    if (bySetPos !== undefined) {
      errors.push(
        buildErrorMessage(
          translate,
          "common.recurrenceErrorUnsupportedBySetPos",
          "bySetPos is only allowed for MONTHLY weekday recurrence",
        ),
      );
    }
  } else if (freq !== "MONTHLY" && byDay) {
    errors.push(
      buildErrorMessage(
        translate,
        "common.recurrenceErrorUnsupportedByDay",
        "byDay is only allowed for WEEKLY recurrence",
      ),
    );
  }

  if (freq === "MONTHLY") {
    const monthlyUsesWeekdayMode = Boolean(byDay || bySetPos);

    if (monthlyUsesWeekdayMode) {
      if (!byDay) {
        errors.push(
          buildErrorMessage(
            translate,
            "common.recurrenceErrorByDayRequiredMonthlyWeekday",
            "byDay is required for MONTHLY weekday recurrence",
          ),
        );
      }
      if (byMonthDay) {
        errors.push(
          buildErrorMessage(
            translate,
            "common.recurrenceErrorMonthlyDayModeConflict",
            "byMonthDay cannot be used with MONTHLY weekday recurrence",
          ),
        );
      }
      if (bySetPos === undefined || bySetPos === null) {
        errors.push(
          buildErrorMessage(
            translate,
            "common.recurrenceErrorBySetPosRequired",
            "bySetPos is required for MONTHLY weekday recurrence",
          ),
        );
      } else if (bySetPos !== -1 && (bySetPos < 1 || bySetPos > 5)) {
        errors.push(
          buildErrorMessage(
            translate,
            "common.recurrenceErrorBySetPosRange",
            "bySetPos must be -1 or between 1 and 5",
          ),
        );
      }

      const start = dayjs(startDate);
      if (start.isValid() && byDay) {
        const weekdayMatches = start.day() === WEEKDAY_ORDER.indexOf(byDay);
        if (!weekdayMatches) {
          const startWeekday = start.format("ddd");
          errors.push(
            buildErrorMessage(
              translate,
              "common.recurrenceErrorMonthlyWeekdayMismatch",
              `Start date weekday (${startWeekday}) must match byDay (${byDay})`,
              { startWeekday, byDay },
            ),
          );
        }
      }

      if (
        start.isValid() &&
        bySetPos !== undefined &&
        bySetPos !== null &&
        byDay
      ) {
        if (!matchesSetPos(start, bySetPos)) {
          errors.push(
            buildErrorMessage(
              translate,
              "common.recurrenceErrorMonthlySetPosMismatch",
              `Start date must be the ${bySetPos === -1 ? "last" : `#${bySetPos}`} occurrence of ${byDay}`,
              { bySetPos, byDay },
            ),
          );
        }
      }
    } else {
      if (!byMonthDay || byMonthDay < 1 || byMonthDay > 31) {
        errors.push(
          buildErrorMessage(
            translate,
            "common.recurrenceErrorByMonthDayRequired",
            "byMonthDay (1-31) is required for MONTHLY recurrence",
          ),
        );
      } else {
        const startDay = dayjs(startDate).date();
        if (startDay !== byMonthDay) {
          const message = MONTHLY_MISMATCH_MESSAGE.replace(
            "%d",
            String(startDay),
          ).replace("%d", String(byMonthDay));
          errors.push(
            buildErrorMessage(
              translate,
              "common.recurrenceErrorMonthlyMismatch",
              message,
              {
                startDay,
                byMonthDay,
              },
            ),
          );
        }
      }
    }
  } else {
    if (byMonthDay) {
      errors.push(
        buildErrorMessage(
          translate,
          "common.recurrenceErrorUnsupportedByMonthDay",
          "byMonthDay is only allowed for MONTHLY recurrence",
        ),
      );
    }
    if (bySetPos !== undefined) {
      errors.push(
        buildErrorMessage(
          translate,
          "common.recurrenceErrorUnsupportedBySetPos",
          "bySetPos is only allowed for MONTHLY weekday recurrence",
        ),
      );
    }
  }

  const baselineEndDate = (() => {
    const minDate =
      minUntilDate && dayjs(minUntilDate).isValid()
        ? dayjs(minUntilDate)
        : null;
    if (minDate) return minDate;
    return dayjs(startDate).isValid() ? dayjs(startDate) : null;
  })();

  if (until && baselineEndDate) {
    const untilDate = dayjs(until);
    if (untilDate.isValid() && untilDate.isBefore(baselineEndDate, "day")) {
      const key =
        minUntilDate && dayjs(minUntilDate).isValid()
          ? "common.recurrenceErrorUntilBeforeRootStart"
          : eventEndDate
            ? "common.recurrenceErrorUntilBeforeEventEndDate"
            : "common.recurrenceErrorUntilBeforeStartDate";
      const fallback =
        minUntilDate && dayjs(minUntilDate).isValid()
          ? "Recurrence end date must be on or after the recurrence start date"
          : eventEndDate
            ? "Recurrence end date must be on or after the event end date"
            : "Recurrence end date must be on or after the event start date";
      errors.push(buildErrorMessage(translate, key, fallback));
    }
  }

  const lastOccurrence = computeLastOccurrence(rule, startDate);
  if (lastOccurrence) {
    const limit = buildGenerationLimit(startDate, freq);
    if (lastOccurrence.isAfter(limit)) {
      const lastOccurrenceFormatted = formatDate(lastOccurrence);
      const limitFormatted = formatDate(limit);
      errors.push(
        buildErrorMessage(
          translate,
          "common.recurrenceErrorExceedsLimit",
          `Recurrence exceeds allowed window (last occurrence ${lastOccurrenceFormatted}, limit ${limitFormatted})`,
          {
            lastOccurrence: lastOccurrenceFormatted,
            limit: limitFormatted,
          },
        ),
      );
    }
  }

  return { isValid: errors.length === 0, errors };
};

export const formatSetPosLabel = (pos: number, translate?: TranslationFn) => {
  const keyMap: Record<number, string> = {
    1: "common.recurrenceSetPosFirst",
    2: "common.recurrenceSetPosSecond",
    3: "common.recurrenceSetPosThird",
    4: "common.recurrenceSetPosFourth",
    5: "common.recurrenceSetPosFifth",
  };
  if (pos === -1) {
    return translate?.("common.recurrenceSetPosLast") ?? "Last";
  }
  const key = keyMap[pos] ?? keyMap[1];
  return translate?.(key, { pos }) ?? `#${pos}`;
};

export const formatWeekdayLabel = (
  weekday: RecurrenceByDay,
  translate?: TranslationFn,
) => {
  return translate
    ? translate(WEEKDAY_LABEL_KEYS[weekday], { weekday })
    : weekday;
};

export const formatRecurrenceSummary = (
  rule: RecurrenceRule | null | undefined,
  _startDate: string,
  translate: TranslationFn,
): string => {
  if (!rule) {
    return translate("common.recurrenceSummaryOneTime");
  }
  const { freq, byDay, byMonthDay, bySetPos, until, count } = rule;

  const weekdayLabel = byDay
    ? formatWeekdayLabel(byDay, translate)
    : (byDay ?? "");

  let base = "";
  if (freq === "DAILY") {
    base = translate("common.recurrenceSummaryDaily");
  } else if (freq === "WEEKLY") {
    base = translate("common.recurrenceSummaryWeekly", {
      weekday: weekdayLabel,
    });
  } else if (freq === "MONTHLY") {
    if (byDay && bySetPos !== undefined && bySetPos !== null) {
      base = translate("common.recurrenceSummaryMonthlyByWeekday", {
        pos: formatSetPosLabel(bySetPos, translate),
        weekday: weekdayLabel,
      });
    } else if (byMonthDay) {
      base = translate("common.recurrenceSummaryMonthlyByDate", {
        day: byMonthDay,
      });
    }
  }

  if (!base) return "";

  if (count) {
    base += translate("common.recurrenceSummaryCount", { count });
  } else if (until) {
    const untilDate = dayjs(until).isValid() ? formatDate(dayjs(until)) : until;
    base += translate("common.recurrenceSummaryUntil", { date: untilDate });
  }

  return base;
};

/**
 * 繰り返しパターンの概要を、終了日情報（until/count）を付けずに返す。
 * 「毎週土曜日」などのルール表記だけを表示したい場合に利用。
 */
export const formatRecurrencePattern = (
  rule: RecurrenceRule | null | undefined,
  translate: TranslationFn,
): string => {
  if (!rule) {
    return translate("common.recurrenceSummaryOneTime");
  }
  const { freq, byDay, byMonthDay, bySetPos } = rule;

  const weekdayLabel =
    byDay && translate ? translate(WEEKDAY_LABEL_KEYS[byDay]) : (byDay ?? "");

  if (freq === "DAILY") {
    return translate("common.daily");
  }
  if (freq === "WEEKLY") {
    return translate("common.recurrenceSummaryWeekly", {
      weekday: weekdayLabel,
    });
  }
  if (freq === "MONTHLY") {
    if (byDay && bySetPos !== undefined && bySetPos !== null) {
      return translate("common.recurrenceSummaryMonthlyByWeekday", {
        pos: formatSetPosLabel(bySetPos, translate),
        weekday: weekdayLabel,
      });
    }
    if (byMonthDay) {
      return translate("common.recurrenceSummaryMonthlyByDate", {
        day: byMonthDay,
      });
    }
  }

  return "";
};
